import React from 'react';
import { TrendingDown, Zap, PiggyBank, Sun, Battery, ArrowRight } from 'lucide-react';

const EconomicBenefitSection = () => {
  const scrollToContact = () => {
    const kontaktElement = document.querySelector('#kontakt');
    if (kontaktElement) {
      kontaktElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section data-testid="economic-benefit-section" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
            Wirtschaftlichkeit
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Wie sich Photovoltaik
            <span className="text-sun-orange"> wirtschaftlich auswirken kann</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Jedes Projekt ist individuell – aber dieses Beispiel zeigt, wie sich eine Photovoltaikanlage 
            mit Speicher auf Ihre Energiekosten auswirken kann.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Before Card */}
          <div className="bg-white rounded-3xl p-8 lg:p-10 border-2 border-slate-200 shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-bl-full opacity-50" />
            
            <div className="relative">
              <div className="inline-flex items-center px-4 py-2 bg-red-100 rounded-full mb-6">
                <TrendingDown className="w-4 h-4 text-red-600 mr-2" />
                <span className="text-red-700 font-semibold text-sm">Ohne Photovoltaik</span>
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-8">Aktuelle Situation</h3>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-5 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-200 rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-slate-500" />
                    </div>
                    <span className="font-medium text-slate-700">Stromverbrauch</span>
                  </div>
                  <span className="text-xl font-bold text-slate-900">4.500 kWh</span>
                </div>
                
                <div className="flex items-center justify-between p-5 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <PiggyBank className="w-6 h-6 text-red-500" />
                    </div>
                    <span className="font-medium text-slate-700">Monatliche Stromkosten</span>
                  </div>
                  <span className="text-xl font-bold text-red-600">ca. 150 €</span>
                </div>
              </div>
              
              <div className="mt-8 p-5 bg-slate-100 rounded-xl">
                <p className="text-slate-600 text-sm">
                  Bei einem durchschnittlichen Strompreis von ca. 40 Cent/kWh und steigender Tendenz.
                </p>
              </div>
            </div>
          </div>

          {/* After Card */}
          <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl p-8 lg:p-10 border-2 border-sun-orange/30 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-sun-orange/10 rounded-bl-full" />
            <div className="absolute inset-0 bg-gradient-to-br from-sun-orange/5 via-transparent to-transparent pointer-events-none" />
            
            <div className="relative">
              <div className="inline-flex items-center px-4 py-2 bg-sun-orange rounded-full mb-6">
                <Sun className="w-4 h-4 text-white mr-2" />
                <span className="text-white font-semibold text-sm">Mit Photovoltaik + Speicher</span>
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-8">Nach der Installation</h3>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-5 bg-white/10 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-sun-orange rounded-xl flex items-center justify-center">
                      <Battery className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-medium text-white/90">Eigenverbrauchsanteil</span>
                  </div>
                  <span className="text-xl font-bold text-sun-orange">ca. 70%</span>
                </div>
                
                <div className="flex items-center justify-between p-5 bg-green-500/20 rounded-xl border border-green-500/30">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                      <PiggyBank className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-medium text-white/90">Neue Stromkosten</span>
                  </div>
                  <span className="text-xl font-bold text-green-400">ca. 40 €</span>
                </div>
              </div>
              
              <div className="mt-8 p-5 bg-white/10 rounded-xl backdrop-blur-sm">
                <p className="text-white/80 text-sm">
                  Durch eigenen Solarstrom und intelligente Speichernutzung sinken Ihre Stromkosten deutlich.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Savings Highlight */}
        <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 border-2 border-green-200 rounded-3xl p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h4 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-3">
                Geschätzte jährliche Ersparnis
              </h4>
              <p className="text-slate-600 max-w-xl">
                Eine Photovoltaikanlage ist eine langfristige Investition in Ihre Energieunabhängigkeit. 
                Die tatsächliche Ersparnis hängt von Ihrem Verbrauch und der Anlagengröße ab.
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="text-5xl lg:text-6xl font-bold text-green-600 mb-2">
                ca. 1.200 €
              </div>
              <div className="text-slate-500 font-medium">pro Jahr</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <button
            onClick={scrollToContact}
            data-testid="economic-benefit-cta"
            className="inline-flex items-center gap-2 bg-sun-orange hover:bg-sun-orange-dark text-white px-8 py-4 rounded-full font-semibold shadow-lg shadow-sun-orange/30 transition-all hover:scale-105"
          >
            Individuelle Berechnung anfragen
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="mt-4 text-slate-500 text-sm">
            Kostenlos und unverbindlich für Ihr Projekt
          </p>
        </div>
      </div>
    </section>
  );
};

export default EconomicBenefitSection;
